import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import './ContentPolicyError.css';

/**
 * ContentPolicyError - A reusable component for displaying content policy violations
 * with the ability to edit the prompt and retry.
 * 
 * @param {Object} props
 * @param {string} props.originalPrompt - The prompt that triggered the violation
 * @param {function} props.onRetry - Callback when user clicks retry with edited prompt
 * @param {function} props.onDismiss - Callback when user dismisses the error
 * @param {boolean} props.isRetrying - Whether a retry is currently in progress
 * @param {string} props.className - Additional CSS class names
 */
function ContentPolicyError({ 
  originalPrompt, 
  onRetry, 
  onDismiss,
  isRetrying = false,
  className = ''
}) {
  const [editedPrompt, setEditedPrompt] = useState(originalPrompt || '');
  
  // Update edited prompt when originalPrompt changes
  useEffect(() => {
    if (originalPrompt) {
      setEditedPrompt(originalPrompt);
    }
  }, [originalPrompt]);

  const handleRetry = () => {
    if (onRetry && editedPrompt.trim()) {
      onRetry(editedPrompt.trim());
    }
  };

  const handleKeyDown = (e) => {
    // Submit on Cmd/Ctrl + Enter
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleRetry();
    }
  };

  const isPromptChanged = editedPrompt.trim() !== (originalPrompt || '').trim();
  const canRetry = editedPrompt.trim().length > 0 && !isRetrying;

  return (
    <div className={`content-policy-error ${className}`}>
      <div className="content-policy-error-header">
        <div className="content-policy-error-icon">
          <AlertTriangle size={20} />
        </div>
        <div className="content-policy-error-title">
          Content Policy Violation
        </div>
        {onDismiss && (
          <button 
            className="content-policy-error-dismiss"
            onClick={onDismiss}
            disabled={isRetrying}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        )}
      </div>
      
      <p className="content-policy-error-message">
        Your prompt contains content that violates our content policy. 
        Please edit your prompt below and try again.
      </p>
      
      <div className="content-policy-error-prompt-section">
        <label className="content-policy-error-label">
          Edit your prompt:
        </label>
        <textarea
          className="content-policy-error-textarea"
          value={editedPrompt}
          onChange={(e) => setEditedPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter a new prompt..."
          disabled={isRetrying}
          rows={3}
        />
        {isPromptChanged && (
          <span className="content-policy-error-hint">
            Prompt modified
          </span>
        )}
      </div>
      
      <div className="content-policy-error-actions">
        <button
          className="content-policy-error-retry-btn"
          onClick={handleRetry}
          disabled={!canRetry}
        >
          {isRetrying ? (
            <>
              <RefreshCw size={14} className="spinning" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw size={14} />
              Retry with Edited Prompt
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ContentPolicyError;

